import React from 'react'

import TextArea from 'presentation/shared/components/TextArea'
import TextField from 'presentation/shared/components/TextField'
import { cpfMask, dateMask, phoneMask } from 'presentation/utils/masks'
import Button from 'presentation/shared/components/Button'
import SelectPendencyStatus from '../SelectPendencyStatus'

import * as S from './styles'

import { useFormik } from 'formik'
import { DoctorRegistrationSteps } from 'common/enum/doctor-overall-registration-steps'
import { toast } from 'react-toastify'
import { translateEthnicity } from 'presentation/utils/translators/ethnicities'
import { observer } from 'mobx-react'
import { DoctorRegisterStepStatus } from 'domain/entities/doctor-register'
import {
  disableObservation,
  generateNewRegistrationDetailsValidationSchema
} from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails'
import { newRegistrationDetailsContext } from 'presentation/hospital/layouts/DoctorRegister/NewRegistrationsDetails/context'
import { UserBirthInfo } from 'domain/entities/user-birth-info-model'

type PersonalDataProps = {
  personalData?: {
    name?: string
    birthDay?: string
    gender?: string
    identityCard?: string
    federativeUnit?: string
    cpf?: string
    expeditionDate?: string
    issuingOrgan?: string
    nationality?: string
    birthInfo?: UserBirthInfo
    ethnicity?: string
    motherName?: string
    fatherName?: string
    badgeName?: string
    directorObservation?: string
  }
  contacts?: {
    email?: string
    phone?: string
    emergencyContact?: string
    emergencyPhone?: string
    directorObservation?: string
  }
  address?: {
    city?: string
    zipCode?: string
    district?: string
    neighborhood?: string
    number?: number
    complement?: string
    homeAddress?: string
    directorObservation?: string
  }
  registrationStatus: registerPendencyValues
  isDirector: boolean
  updateStepStatus?: (step: any) => Promise<void>
}

const PersonalData = observer(
  ({
    personalData,
    contacts,
    address,
    registrationStatus,
    updateStepStatus,
    isDirector
  }: PersonalDataProps) => {
    const { isStepsUpdateBlocked } = newRegistrationDetailsContext
    const { handleChange, values, setFieldValue, handleSubmit, isValid } =
      useFormik({
        initialValues: registrationStatus,
        validateOnMount: true,
        enableReinitialize: true,
        validationSchema: generateNewRegistrationDetailsValidationSchema(
          DoctorRegistrationSteps.PERSONAL_DATA,
          DoctorRegistrationSteps.ADDRESS,
          DoctorRegistrationSteps.CONTACTS
        ),
        onSubmit: async (values) => {
          try {
            await updateStepStatus?.(values)
            toast.success('Dados salvos com sucesso.')
            window.scrollTo(0, 0)
          } catch (error) {
            toast.error('Erro ao salvar dados.')
            return
          }
        }
      })

    const setPendencyStatus = (field: string, event: any) => {
      if (isDirector) {
        setFieldValue(
          `${field}.status`,
          event.target.value === 'Revisão'
            ? DoctorRegisterStepStatus.REVIEW
            : DoctorRegisterStepStatus.APPROVED_DIRECTOR
        )
      } else {
        setFieldValue(
          `${field}.status`,
          event.target.value === 'Pendente'
            ? DoctorRegisterStepStatus.PENDENT
            : DoctorRegisterStepStatus.APPROVED_DOCTOR_REGISTER
        )
      }
    }

    return (
      <S.Wrapper data-testid="card-personal-data">
        <S.PersonalDataContainer>
          <S.PendencySelection>
            <SelectPendencyStatus
              handleChange={(e) =>
                setPendencyStatus(DoctorRegistrationSteps.PERSONAL_DATA, e)
              }
              initialValue={
                values[DoctorRegistrationSteps.PERSONAL_DATA].status
              }
              disabled={isStepsUpdateBlocked}
              data-testid="select-pendency-status-personal-data"
              isDirector={isDirector}
            />
          </S.PendencySelection>
          <S.Title>Dados pessoais</S.Title>
          <S.InputsContainer>
            <TextField
              label="Nome completo"
              value={personalData?.name ?? ''}
              required
              disabled
              data-testid="input-name"
            />
            <section>
              <TextField
                label="Data de nascimento"
                value={personalData?.birthDay ?? ''}
                required
                mask={dateMask}
                disabled
                data-testid="input-birthday"
              />
              <TextField
                label="Sexo"
                value={personalData?.gender ?? ''}
                required
                disabled
                data-testid="input-gender"
              />
            </section>
            <section>
              <TextField
                label="RG"
                value={personalData?.identityCard ?? ''}
                required
                disabled
                data-testid="input-rg"
              />
              <TextField
                label="CPF"
                value={personalData?.cpf ?? ''}
                required
                disabled
                mask={cpfMask}
                data-testid="input-cpf"
              />
            </section>

            <TextField
              label="Cidade"
              value={personalData?.birthInfo?.cityName ?? ''}
              required
              disabled
              data-testid="input-birth-info-city"
            />
            <TextField
              label="Naturalidade"
              value={personalData?.birthInfo?.stateName ?? ''}
              required
              disabled
              data-testid="input-birth-info-state"
            />
            <TextField
              label="Como o profissional se identifica"
              value={translateEthnicity(personalData?.ethnicity ?? '')}
              required
              disabled
              data-testid="input-ethnicity"
            />
            <TextField
              label="Nome da mãe"
              value={personalData?.motherName ?? ''}
              required
              disabled
              data-testid="input-mother-name"
            />
            <TextField
              label="Nome do pai"
              value={personalData?.fatherName ?? ''}
              required
              disabled
              data-testid="input-father-name"
            />
            <TextField
              label="Nome que será utilizado no crachá"
              value={personalData?.badgeName ?? ''}
              required
              disabled
              data-testid="input-badge-name"
            />
          </S.InputsContainer>
          <S.DirectorObservations>
            <TextArea
              label="Observações do diretor"
              name={`${DoctorRegistrationSteps.PERSONAL_DATA}.director_review`}
              value={
                values[DoctorRegistrationSteps.PERSONAL_DATA].director_review
              }
              onChange={handleChange}
              required
              disabled={
                !isDirector ||
                values[DoctorRegistrationSteps.PERSONAL_DATA].status !==
                  DoctorRegisterStepStatus.REVIEW
              }
              data-testid="input-director-observation-personal-data"
            />
          </S.DirectorObservations>
          {!isDirector && (
            <S.PendencyJustification>
              <TextArea
                name={`${DoctorRegistrationSteps.PERSONAL_DATA}.description`}
                label="No caso de pendência, favor descrever"
                value={
                  values[DoctorRegistrationSteps.PERSONAL_DATA].description
                }
                onChange={handleChange}
                data-testid="input-pendency-personal-data"
                disabled={
                  disableObservation(DoctorRegistrationSteps.PERSONAL_DATA) ||
                  values[DoctorRegistrationSteps.PERSONAL_DATA].status !==
                    DoctorRegisterStepStatus.PENDENT ||
                  isStepsUpdateBlocked
                }
                required
              />
            </S.PendencyJustification>
          )}
        </S.PersonalDataContainer>
        <S.ContactsContainer>
          <S.PendencySelection>
            <SelectPendencyStatus
              handleChange={(e) =>
                setPendencyStatus(DoctorRegistrationSteps.CONTACTS, e)
              }
              initialValue={values[DoctorRegistrationSteps.CONTACTS].status}
              data-testid="select-pendency-status-contacts"
              disabled={isStepsUpdateBlocked}
              isDirector={isDirector}
            />
          </S.PendencySelection>
          <S.Title>Contatos</S.Title>
          <S.InputsContainer>
            <TextField
              label="E-mail"
              value={contacts?.email ?? ''}
              required
              disabled
              data-testid="input-email"
            />
            <TextField
              label="Telefone celular"
              value={contacts?.phone ?? ''}
              required
              disabled
              mask={phoneMask}
              data-testid="input-phone"
            />
            <TextField
              label="Nome do contato de emergência"
              value={contacts?.emergencyContact ?? ''}
              required
              disabled
              data-testid="input-emergency-contact"
            />
            <TextField
              label="Telefone de emergência"
              value={contacts?.emergencyPhone ?? ''}
              mask={phoneMask}
              required
              disabled
              data-testid="input-emergency-phone"
            />
          </S.InputsContainer>
          <S.DirectorObservations>
            <TextArea
              label="Observações do diretor"
              name={`${DoctorRegistrationSteps.CONTACTS}.director_review`}
              value={values[DoctorRegistrationSteps.CONTACTS].director_review}
              onChange={handleChange}
              required
              disabled={
                !isDirector ||
                values[DoctorRegistrationSteps.CONTACTS].status !==
                  DoctorRegisterStepStatus.REVIEW
              }
              data-testid="input-director-observation-contacts"
            />
          </S.DirectorObservations>
          {!isDirector && (
            <S.PendencyJustification>
              <TextArea
                name={`${DoctorRegistrationSteps.CONTACTS}.description`}
                label="No caso de pendência, favor descrever"
                value={values[DoctorRegistrationSteps.CONTACTS].description}
                onChange={handleChange}
                data-testid="input-pendency-contacts"
                disabled={
                  disableObservation(DoctorRegistrationSteps.CONTACTS) ||
                  values[DoctorRegistrationSteps.CONTACTS].status !==
                    DoctorRegisterStepStatus.PENDENT ||
                  isStepsUpdateBlocked
                }
                required
              />
            </S.PendencyJustification>
          )}
        </S.ContactsContainer>
        <S.AddressContainer>
          <S.PendencySelection>
            <SelectPendencyStatus
              handleChange={(e) =>
                setPendencyStatus(DoctorRegistrationSteps.ADDRESS, e)
              }
              initialValue={values[DoctorRegistrationSteps.ADDRESS].status}
              data-testid="select-pendency-status-address"
              disabled={isStepsUpdateBlocked}
              isDirector={isDirector}
            />
          </S.PendencySelection>
          <S.Title>Endereço</S.Title>
          <S.InputsContainer>
            <TextField
              label="Cidade"
              value={address?.city ?? ''}
              required
              disabled
              data-testid="input-address-city"
            />
            <TextField
              label="CEP"
              value={address?.zipCode ?? ''}
              required
              disabled
              data-testid="input-address-zip-code"
            />
            <TextField
              label="UF"
              value={address?.district ?? ''}
              required
              disabled
              data-testid="input-address-district"
            />
            <TextField
              label="Bairro"
              value={address?.neighborhood ?? ''}
              required
              disabled
              data-testid="input-address--neighborhood"
            />
          </S.InputsContainer>
          <S.InputsContainer>
            <TextField
              label="Número"
              value={address?.number ?? ''}
              required
              disabled
              data-testid="input-address-number"
            />
            <TextField
              label="Complemento"
              value={address?.complement ?? ''}
              required
              disabled
              data-testid="input-address-complement"
            />
            <TextField
              label="Endereço residencial"
              value={address?.homeAddress ?? ''}
              required
              disabled
              data-testid="input-address-home-address"
            />
          </S.InputsContainer>
          <S.DirectorObservations>
            <TextArea
              label="Observações do diretor"
              name={`${DoctorRegistrationSteps.ADDRESS}.director_review`}
              value={values[DoctorRegistrationSteps.ADDRESS].director_review}
              onChange={handleChange}
              required
              disabled={
                !isDirector ||
                values[DoctorRegistrationSteps.ADDRESS].status !==
                  DoctorRegisterStepStatus.REVIEW
              }
              data-testid="input-director-observation-address"
            />
          </S.DirectorObservations>
          {!isDirector && (
            <S.PendencyJustification>
              <TextArea
                name={`${DoctorRegistrationSteps.ADDRESS}.description`}
                label="No caso de pendência, favor descrever"
                value={values[DoctorRegistrationSteps.ADDRESS].description}
                onChange={handleChange}
                data-testid="input-pendency-address"
                disabled={
                  disableObservation(DoctorRegistrationSteps.ADDRESS) ||
                  values[DoctorRegistrationSteps.ADDRESS].status !==
                    DoctorRegisterStepStatus.PENDENT ||
                  isStepsUpdateBlocked
                }
                required
              />
            </S.PendencyJustification>
          )}
        </S.AddressContainer>
        <S.ActionsButtons>
          <Button
            data-testid="btn-save-personal-data"
            onClick={() => handleSubmit()}
            variant={isValid && !isStepsUpdateBlocked ? 'outlined' : undefined}
            disabled={!isValid || isStepsUpdateBlocked}
          >
            Salvar
          </Button>
        </S.ActionsButtons>
      </S.Wrapper>
    )
  }
)

export default PersonalData

type registerPendencyValues = {
  [DoctorRegistrationSteps.PERSONAL_DATA]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }

  [DoctorRegistrationSteps.ADDRESS]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }

  [DoctorRegistrationSteps.CONTACTS]: {
    description?: string
    director_review?: string
    status: DoctorRegisterStepStatus
  }
}
